import { useMemo } from "react";
import { Link } from "react-router";
import { StatCards, type Stat } from "@repo/blocks";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { PageScaffold } from "../shell/PageScaffold";
import { useVenue } from "../shell/VenueContext";
import { useMinuteClock } from "../shell/useMinuteClock";
import { ShellIcon, type IconName } from "../shell/icons";
import { StatusBadge } from "../bookings/Badges";
import { bookingClock, guestName, isNum, todayServiceDay } from "../bookings/format";
import { flattenPages, useBookingsList, useInbox } from "../bookings/queries";
import { summariseToday } from "./overview/todaySummary";

/**
 * The venue landing screen — a "tonight at a glance" dashboard. It reuses the ONE booking query layer
 * (today's list + the request inbox) so it stays live off the same SSE invalidation as the rest of the
 * shell, reduces the rows to headline numbers (covers / bookings / arrivals / pending) with
 * `summariseToday`, lists the next arrivals, and offers quick links into the day's working surfaces.
 */
export function VenueOverview() {
  const { venueId, venue } = useVenue();
  const { t, i18n } = useTranslation();
  const today = todayServiceDay(venue.timeZone);

  const bookings = useBookingsList(venueId, { from: today, to: today, status: [], channel: [] });
  const inbox = useInbox(venueId);

  const rows = flattenPages(bookings.data);
  const pendingCount = flattenPages(inbox.data).length;
  // Not `Date.now()` inline — react-compiler forbids impure calls during render (react(purity)).
  const nowSec = useMinuteClock();
  const summary = useMemo(() => summariseToday(rows, nowSec), [rows, nowSec]);

  const stats: Stat[] = [
    {
      label: t("shell.overview.stat.covers"),
      value: summary.covers,
      icon: <ShellIcon name="covers" />,
    },
    {
      label: t("shell.overview.stat.bookings"),
      value: summary.bookingCount,
      icon: <ShellIcon name="bookings" />,
    },
    {
      label: t("shell.overview.stat.upcoming"),
      value: summary.upcomingCount,
      icon: <ShellIcon name="arrivals" />,
    },
    {
      label: t("shell.overview.stat.pending"),
      value: pendingCount,
      icon: <ShellIcon name="requests" />,
    },
  ];

  return (
    <PageScaffold title={venue.displayName}>
      <p className={css({ color: "fg.muted", mt: "-2" })}>{t("shell.overview.subtitle")}</p>

      <StatCards stats={stats} />

      <div
        className={css({
          display: "grid",
          gap: "6",
          gridTemplateColumns: { base: "1fr", lg: "2fr 1fr" },
          alignItems: "start",
        })}
      >
        <Panel title={t("shell.overview.arrivals.title")}>
          {bookings.isLoading ? (
            <p className={css({ fontSize: "sm", color: "fg.muted" })}>
              {t("bookings.list.loading")}
            </p>
          ) : summary.nextArrivals.length === 0 ? (
            <div className={css({ fontSize: "sm", color: "fg.muted", py: "2" })}>
              <p>{t("shell.overview.arrivals.empty")}</p>
              <Link
                to={`/v/${venueId}/bookings`}
                className={css({
                  color: "colorPalette.text",
                  textDecoration: "underline",
                  fontWeight: "medium",
                })}
              >
                {t("shell.overview.arrivals.cta")}
              </Link>
            </div>
          ) : (
            <ul className={css({ display: "flex", flexDirection: "column" })}>
              {summary.nextArrivals.map((b) => (
                <li
                  key={b.bookingId}
                  className={css({
                    display: "flex",
                    alignItems: "center",
                    gap: "3",
                    py: "2.5",
                    borderBottomWidth: "1px",
                    borderColor: "border.subtle",
                    _last: { borderBottomWidth: "0" },
                  })}
                >
                  <span
                    className={css({
                      fontVariantNumeric: "tabular-nums",
                      fontWeight: "semibold",
                      minW: "14",
                    })}
                  >
                    {isNum(b.startSec)
                      ? bookingClock(b.startSec, venue.timeZone, i18n.language)
                      : "—"}
                  </span>
                  <span className={css({ flex: "1", minW: "0", truncate: true })}>
                    {guestName(b, t("bookings.noName"))}
                  </span>
                  <span className={css({ fontSize: "sm", color: "fg.muted", minW: "10" })}>
                    {t("shell.overview.arrivals.party", { count: b.partySize ?? 0 })}
                  </span>
                  <StatusBadge status={b.status} />
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title={t("shell.overview.quickLinks.title")}>
          <div className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
            <QuickLink
              to={`/v/${venueId}/bookings`}
              icon="bookings"
              label={t("shell.overview.quickLinks.bookings")}
            />
            <QuickLink
              to={`/v/${venueId}/live`}
              icon="live"
              label={t("shell.overview.quickLinks.live")}
            />
            <QuickLink
              to={`/v/${venueId}/floor-plan`}
              icon="floorPlan"
              label={t("shell.overview.quickLinks.floor")}
            />
            <QuickLink
              to={`/v/${venueId}/waitlist`}
              icon="waitlist"
              label={t("shell.overview.quickLinks.waitlist")}
            />
            <QuickLink
              to={`/v/${venueId}/settings/channels?tab=share`}
              icon="embed"
              label={t("shell.overview.quickLinks.share")}
            />
          </div>
        </Panel>
      </div>
    </PageScaffold>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      className={css({
        borderWidth: "1px",
        borderColor: "border.default",
        rounded: "lg",
        bg: "bg.default",
        p: "4",
        display: "flex",
        flexDirection: "column",
        gap: "3",
      })}
    >
      <h2 className={css({ fontSize: "sm", fontWeight: "semibold", color: "fg.default" })}>
        {title}
      </h2>
      {children}
    </section>
  );
}

function QuickLink({ to, icon, label }: { to: string; icon: IconName; label: string }) {
  return (
    <Link
      to={to}
      className={css({
        display: "flex",
        alignItems: "center",
        gap: "3",
        px: "3",
        py: "2",
        rounded: "md",
        fontSize: "sm",
        fontWeight: "medium",
        color: "fg.default",
        _hover: { bg: "bg.muted" },
      })}
    >
      <span
        className={css({ fontSize: "md", color: "colorPalette.default", display: "inline-flex" })}
      >
        <ShellIcon name={icon} />
      </span>
      {label}
    </Link>
  );
}
